<template>
  <div class="animated fadeIn">
    <div class="card shadow-sm border-0">
      <div class="card-header bg-facebook text-white d-flex align-items-center justify-content-between py-2">
        <h6 class="mb-0"><font-awesome-icon icon="check-square" /> Office Time Log For Approval</h6>
        <small class="text-white-50 text-uppercase font-weight-bold">Processed batches awaiting approval</small>
      </div>
      <div class="card-body position-relative">
        <b-row align-v="center" class="loader" v-if="loading">
          <b-col class="text-center">
            <b-spinner></b-spinner>
            <div class="mt-2"><strong>Loading</strong></div>
          </b-col>
        </b-row>

        <b-form-select v-model="filter_group_id" :options="filterOptions" size="sm" class="mb-3" style="max-width:320px" @change="getAll"></b-form-select>

        <b-table
          responsive
          hover
          striped
          bordered
          small
          :items="list.data"
          :fields="fields"
          :busy="loading"
          show-empty
          head-variant="dark"
        >
          <template v-slot:cell(group)="data">
            <b-badge variant="dark">{{ data.item.payroll_group ? data.item.payroll_group.name : '-' }}</b-badge>
          </template>
          <template v-slot:cell(period)="data">
            <strong>{{ data.item.formatted_from_date.other_formats.format_1 }}</strong>
            &mdash;
            <strong>{{ data.item.formatted_to_date.other_formats.format_1 }}</strong>
          </template>
          <template v-slot:cell(count)="data">
            <b-badge variant="info">{{ data.item.time_logs.length }} record(s)</b-badge>
          </template>
          <template v-slot:cell(action)="data">
            <div class="text-center">
              <b-button variant="primary" size="sm" @click="view(data.item)">
                <font-awesome-icon icon="list" /> View
              </b-button>
              <b-button variant="outline-danger" size="sm" @click="remove(data.item)">
                <font-awesome-icon icon="minus-circle" /> Delete
              </b-button>
            </div>
          </template>
        </b-table>

        <nav v-if="list.total > 0">
          <b-pagination
            :disabled="loading"
            @input="getAll"
            align="right"
            :total-rows="total_rows"
            :per-page="per_page"
            v-model="list.current_page"
            class="mb-0"
          ></b-pagination>
        </nav>
      </div>
    </div>

    <b-modal v-model="modal" centered size="xl" modal-class="xxl-modal" body-class="p-4" :hide-footer="true" :hide-header="true">
      <div class="d-flex align-items-center justify-content-between mb-3" v-if="record.id">
        <div>
          <h5 class="mb-0"><font-awesome-icon icon="calendar" /> Batch #{{ record.id }} <b-badge variant="dark" class="ml-1" v-if="record.payroll_group">{{ record.payroll_group.name }}</b-badge></h5>
          <small class="text-muted">
            {{ record.formatted_from_date.other_formats.format_1 }} &mdash; {{ record.formatted_to_date.other_formats.format_1 }}
          </small>
        </div>
        <b-button variant="success" size="sm" @click="approve">
          <font-awesome-icon icon="save" /> Approve Batch
        </b-button>
      </div>

      <div v-for="(day, di) in result" :key="di" class="mb-3">
        <div class="bg-dark text-white p-2 rounded-top">
          <font-awesome-icon icon="calendar" /> {{ day.date.other_formats.format_1 }}
        </div>
        <div class="table-responsive">
          <table class="table table-bordered table-sm mb-0 text-center align-middle">
            <thead class="thead-light">
              <tr>
                <th class="text-left">Employee</th>
                <th>AM IN</th>
                <th>AM OUT</th>
                <th>PM IN</th>
                <th>PM OUT</th>
                <th>OT IN</th>
                <th>OT OUT</th>
                <th>Late (min)</th>
                <th>Reg Hrs</th>
                <th>OT Hrs</th>
                <th>ND Hrs</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(log, li) in day.logs" :key="li">
                <td class="text-left">
                  <div class="font-weight-bold text-uppercase">{{ log.other_details.manpower_details.full_name }}</div>
                  <b-badge variant="info">ID : {{ log.other_details.manpower_details.id_number }}</b-badge>
                </td>
                <td v-for="p in ['am_in', 'am_out', 'pm_in', 'pm_out', 'ot_in', 'ot_out']" :key="p">
                  {{ timeText(log.other_details['formatted_' + p]) }}
                  <div v-if="log.other_details.punch_audit && log.other_details.punch_audit[p] && log.other_details.punch_audit[p].source === 'manual'"
                       class="small text-warning text-nowrap"
                       :title="'Set manually' + (log.other_details.punch_audit[p].edited_by ? ' by ' + log.other_details.punch_audit[p].edited_by : '') + (log.other_details.punch_audit[p].edited_at ? ' on ' + log.other_details.punch_audit[p].edited_at : '')">
                    <font-awesome-icon icon="pencil-alt" /> manual
                    <span class="text-muted" v-if="log.other_details.punch_audit[p].original">&middot; scan <s>{{ log.other_details.punch_audit[p].original }}</s></span>
                  </div>
                </td>
                <td :class="log.late_minutes > 0 ? 'text-danger font-weight-bold' : ''">{{ log.late_minutes }}</td>
                <td>{{ log.regular_hours }}</td>
                <td>{{ log.ot_hours }}</td>
                <td>{{ log.nd_hours }}</td>
                <td class="font-weight-bold">{{ log.total }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="text-right mt-3" v-if="record.id">
        <b-button variant="secondary" size="sm" @click="modal = false" class="mr-2">Close</b-button>
        <b-button variant="success" size="sm" @click="approve">
          <font-awesome-icon icon="save" /> Approve Batch
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'

  export default {
    name: 'OfficeForApproval',
    mixins: [SwalMixin],
    data() {
      return {
        loading: true,
        groups: [],
        filter_group_id: null,
        list: { data: [], current_page: 1, total: 0, per_page: 1 },
        per_page_size: 10,
        fields: [
          { key: 'id', label: 'Batch #' },
          { key: 'group', label: 'Payroll Group' },
          { key: 'period', label: 'Period' },
          { key: 'count', label: 'Records' },
          { key: 'action', label: '' },
        ],
        modal: false,
        record: {},
        result: [],
      }
    },
    computed: {

      filterOptions() {

        return [{ value: null, text: 'All payroll groups' }].concat(this.groups.map(g => ({ value: g.id, text: g.name })));

      },
      per_page() { return parseInt(this.list.per_page); },
      total_rows() { return parseInt(this.list.total); },
    },
    created() {
      this.getGroups();
      this.getAll();
    },
    methods: {
      getGroups() {
        this.axios.get('payroll-groups/options').then(response => { this.groups = response.data || []; }).catch(error => console.log(error));
      },
      timeText(formatted) {
        if (!formatted || formatted.default === '') return '--';
        return formatted.other_formats.format_1;
      },
      getAll() {
        this.loading = true;
        this.axios.get('office-timelog-logs/for-approval', { params: { page: this.list.current_page, per_page: this.per_page_size, payroll_group_id: this.filter_group_id } })
          .then(response => {
            this.list = response.data;
            this.loading = false;
          }).catch(error => {
            console.log(error);
            this.loading = false;
          });
      },
      view(item) {
        this.record = item;
        this.result = [];
        this.loading = true;
        this.axios.get('office-timelog-logs/time-logs', { params: { id: item.id } }).then(response => {
          this.result = response.data;
          this.modal = true;
          this.loading = false;
        }).catch(error => {
          this.loading = false;
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
          this.swalRequestError();
        });
      },
      approve() {
        this.swalConfirmSubmit((data) => {
          if (data.value) {
            this.loading = true;
            this.axios.post('office-timelog-logs/approve', { json_import: this.record, result: this.result }).then(() => {
              this.swal_transaction_completed_data.text = 'Batch approved.';
              this.swalTransactionCompleted();
              this.modal = false;
              this.getAll();
            }).catch(error => {
              this.loading = false;
              this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
              this.swalRequestError();
            });
          }
        });
      },
      remove(item) {
        this.swalConfirmDelete((data) => {
          if (data.value) {
            this.loading = true;
            this.axios.delete('office-timelog-logs/delete-import', { data: { id: item.id } }).then(() => {
              this.swal_transaction_completed_data.text = 'Batch deleted. Its records are available again.';
              this.swalTransactionCompleted();
              this.getAll();
            }).catch(error => {
              this.loading = false;
              this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
              this.swalRequestError();
            });
          }
        });
      }
    }
  }
</script>
